
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Divider,
  Paper,
  Stepper,
  Step,
  StepButton,
  StepConnector,
  stepConnectorClasses,
  Chip,
  Avatar,
  LinearProgress,
  CircularProgress,
} from '@mui/material';

import { styled } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import axios from 'axios';
import { toast } from 'react-toastify';

const steps = [
  {
    label: 'Identité & Fiche patient (e)',
    subtitle: 'Coordonnées et informations civiles du patient',
    icon: PersonOutlineIcon,
  },
  {
    label: 'Chemin de Vie',
    subtitle: 'Contexte personnel et parcours de vie',
    icon: AltRouteIcon,
  },
  {
    label: 'Parcours Médical & Hygiène',
    subtitle: 'Historique médical, traitements et mode de vie',
    icon: MonitorHeartIcon,
  },
  {
    label: 'Documents & Compte Client',
    subtitle: 'Pièces jointes et accès au portail patient',
    icon: DescriptionOutlinedIcon,
  },
];

const ColorConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 18,
  },

  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#E0E5F2',
    borderTopWidth: 2,
    borderRadius: 1,
  },

  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: '#4318FF',
  },

  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: '#4318FF',
  },
}));

const StepIconRoot = styled('div')(({ ownerState }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  backgroundColor:
    ownerState.active || ownerState.completed ? '#4318FF' : '#EDF0F8',

  color:
    ownerState.active || ownerState.completed ? '#fff' : '#A3AED0',

  transition: 'background-color 0.2s ease',
}));

const CustomStepIcon = (props) => {
  const { active, completed, icon } = props;

  const StepIconComponent =
    steps[icon - 1]?.icon || PersonOutlineIcon;

  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? (
        <CheckCircleIcon fontSize="small" />
      ) : (
        <StepIconComponent fontSize="small" />
      )}
    </StepIconRoot>
  );
};

const SectionHeader = ({ title, description }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontWeight: 700,
        color: '#2B3674',
        fontSize: '0.95rem',
      }}
    >
      {title}
    </Typography>

    {description && (
      <Typography
        sx={{
          color: '#A3AED0',
          fontSize: '0.8rem',
          mt: 0.25,
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);

const emptyFormData = {
  nom_patient: '',
  prenom_patient: '',
  email_patient: '',
  telephone_patient: '',
  lieu_residence_patient: '',
  date_naissance_patient: '',
  profession_patient: '',
  statut_pro_patient: 'actif',
  situation_familiale_patient: '',
  enfants_age_patient: '',
  personne_confiance_lien_prenom: '',
  personne_confiance_tel: '',
  connaissance_soa: '',
  premiere_visite_date: '',
  coordination_principale: '',

  date_entretien: '',
  pratique_ou_coordination: '',
  coordinatrices: '',

  enfance_famille_naissance: '',
  vie_couple_famille: '',
  vie_professionnelle: '',
  vie_aujourd_hui: '',
  resume_chemin_de_vie: '',

  parcours_general: '',
  origine_probable_decrite: '',
  deroule_cancer: '',
  pathologie_patient: '',
  corps_medical: '',
  etat_sante_aujourd_hui: '',
  effets_indesirables_physique: '',
  effets_indesirables_psy_emo: '',
  analyse_medicale: '',
  traitement_allopathique: '',
  traitement_alternatif: '',

  alimentation: '',
  allergies: '',
  activites_physiques: '',
  sommeil: '',
  loisirs: '',

  attentes_soa: '',
  situation_financiere: 'ras',
  notes_financieres: '',
  autre_info: '',

  // Compte client
  email_client: '',
  password_client: '',
  addresse_client: '',
  ville_client: '',
  cp_client: '',
  phone_client: '',
};

const toDateInput = (value) =>
  value ? String(value).slice(0, 10) : '';

/**
 * Transforme la réponse API :
 *
 * {
 *   patient,
 *   fiche_patient: {...},
 *   client: {...}
 * }
 *
 * en un seul formData.
 */
const flattenPatientResponse = (raw) => {
  const ficheData = raw?.fiche_patient || {};

  const {
    documents,
    id_fiche_patient,
    patient_profile_id,
    created_at: _c1,
    updated_at: _u1,
    ...ficheFields
  } = ficheData;

  const {
    fiche_patient,
    created_at: _c2,
    updated_at: _u2,
    client,
    ...patientFields
  } = raw || {};

  const mergedFields = {
    ...patientFields,
    ...ficheFields,
  };

  const cleanedFields = Object.fromEntries(
    Object.entries(mergedFields).map(([key, value]) => [
      key,
      value === null || value === undefined ? '' : value,
    ])
  );

  return {
    formData: {
      ...emptyFormData,

      ...cleanedFields,

      date_naissance_patient: toDateInput(
        patientFields.date_naissance_patient
      ),

      premiere_visite_date: toDateInput(
        patientFields.premiere_visite_date
      ),

      date_entretien: toDateInput(
        ficheFields.date_entretien
      ),

      // Informations du compte client
      email_client: client?.email_client || '',
      addresse_client: client?.addresse_client || '',
      ville_client: client?.ville_client || '',
      cp_client: client?.cp_client || '',
      phone_client: client?.phone_client || '',

      // Pour des raisons de sécurité :
      // on ne récupère jamais le mot de passe existant.
      password_client: '',
    },

    existingDocuments: documents || [],
  };
};

const EditPatientDialog = ({
  open,
  patientId,
  onClose,
  onPatientUpdated,
  apiUrl,
  token,
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(emptyFormData);

  const [existingDocuments, setExistingDocuments] = useState([]);

  const [photoFile, setPhotoFile] = useState(null);
  const [currentPhotoPath, setCurrentPhotoPath] = useState(null);

  const [documentFiles, setDocumentFiles] = useState([]);

  const [activeField, setActiveField] = useState(null);

  /**
   * URL complète pour les fichiers stockés.
   */
  const getFullUrl = (path) => {
    if (!path) return null;

    if (
      path.startsWith('blob:') ||
      path.startsWith('http')
    ) {
      return path;
    }

    const storageBase = apiUrl
      ? apiUrl.replace('/api', '')
      : '';

    return `${storageBase}/storage/${path}`;
  };

  /**
   * Chargement du dossier patient.
   *
   * IMPORTANT :
   * Cette requête est uniquement GET.
   * Elle ne modifie absolument rien en base.
   */
  useEffect(() => {
    if (!open || !patientId) return;

    let cancelled = false;

    const loadPatient = async () => {
      setFetching(true);
      setActiveStep(0);
      setErrors({});
      setPhotoFile(null);
      setDocumentFiles([]);

      try {
        const response = await axios.get(
          `${apiUrl}/admin/patients/${patientId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const raw =
          response.data?.data || response.data;

        const {
          formData: flatData,
          existingDocuments: docs,
        } = flattenPatientResponse(raw);

        if (!cancelled) {
          setFormData(flatData);
          setExistingDocuments(docs);

          setCurrentPhotoPath(
            raw?.photo_patient || null
          );
        }
      } catch (err) {
        console.error(
          'Erreur chargement patient :',
          err
        );

        toast.error(
          'Erreur lors du chargement du dossier patient'
        );

        if (!cancelled) {
          onClose();
        }
      } finally {
        if (!cancelled) {
          setFetching(false);
        }
      }
    };

    loadPatient();

    return () => {
      cancelled = true;
    };
  }, [open, patientId, apiUrl, token]);

  /**
   * Modification d'un champ.
   *
   * IMPORTANT :
   * aucun appel API ici.
   * Toutes les modifications restent uniquement
   * dans le state React jusqu'au bouton final.
   */
  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  /**
   * Ajout de nouveaux documents.
   */
  const handleAddDocuments = (e) => {
    const newFiles = Array.from(
      e.target.files || []
    );

    setDocumentFiles((prev) => [
      ...prev,
      ...newFiles,
    ]);

    e.target.value = '';
  };

  /**
   * Suppression d'un document nouvellement ajouté.
   */
  const handleRemoveNewDocument = (index) => {
    setDocumentFiles((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  /**
   * Validation des champs obligatoires.
   *
   * Pour rester identique à AddPatientDialog :
   * uniquement nom + prénom obligatoires.
   */
  const validateStep = (step) => {
    if (step === 0) {
      const newErrors = {};

      if (!formData.nom_patient?.trim()) {
        newErrors.nom_patient =
          'Le nom est requis';
      }

      if (!formData.prenom_patient?.trim()) {
        newErrors.prenom_patient =
          'Le prénom est requis';
      }

      setErrors(newErrors);

      return (
        Object.keys(newErrors).length === 0
      );
    }

    return true;
  };

  /**
   * Bouton "Suivant".
   *
   * Aucun appel API.
   */
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  /**
   * Bouton "Retour".
   *
   * Aucun appel API.
   */
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  /**
   * Navigation dans le Stepper.
   *
   * On autorise uniquement le retour vers une étape déjà visitée.
   *
   * Cela évite de pouvoir passer directement de l'étape 1
   * à l'étape 4 sans utiliser "Suivant".
   */
  const handleStepClick = (stepIndex) => (e) => {
    e.preventDefault();

    if (stepIndex <= activeStep) {
      setActiveStep(stepIndex);
    }
  };

  /**
   * Empêche la touche Entrée de déclencher
   * accidentellement l'enregistrement.
   *
   * Les textarea gardent leur comportement normal.
   */
  const handleFormKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      e.target.tagName !== 'TEXTAREA'
    ) {
      e.preventDefault();
    }
  };

  /**
   * ENREGISTREMENT FINAL
   *
   * C'est le SEUL endroit où l'UPDATE est envoyé.
   *
   * Pas de PUT/PATCH pendant :
   * - handleChange
   * - handleNext
   * - handleBack
   * - handleStepClick
   *
   * L'utilisateur peut donc modifier les 4 étapes
   * tranquillement puis cliquer une seule fois
   * sur "Enregistrer les modifications".
   */
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    /**
     * Sécurité supplémentaire :
     * aucune sauvegarde si on n'est pas sur l'étape 4.
     */
    if (activeStep !== steps.length - 1) {
      return;
    }

    /**
     * Vérification des champs obligatoires.
     */
    if (!validateStep(0)) {
      setActiveStep(0);

      toast.error(
        'Merci de compléter les champs obligatoires'
      );

      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      /**
       * Laravel :
       *
       * POST + _method=PUT
       *
       * permet de conserver correctement
       * les fichiers multipart.
       */
      data.append('_method', 'POST');

      /**
       * Champs qui ne doivent jamais être envoyés
       * depuis la boucle générique.
       */
      const excludedKeys = [
        'photo_patient',
        'id_patient_profile',
        'client_id',
        'fiche_patient',
        'created_at',
        'updated_at',
      ];

      /**
       * Tous les champs patient + fiche + client
       * sont envoyés ici.
       */
      Object.keys(formData).forEach((key) => {
        if (excludedKeys.includes(key)) {
          return;
        }

        const value = formData[key];

        /**
         * On ne transmet pas les valeurs vides.
         *
         * IMPORTANT :
         * password_client vide = le backend
         * conservera l'ancien mot de passe.
         */
        if (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== false
        ) {
          data.append(key, value);
        }
      });

      /**
       * PHOTO
       *
       * Si l'utilisateur sélectionne une nouvelle photo :
       * elle est envoyée.
       *
       * Sinon :
       * aucune photo n'est envoyée et le backend
       * conserve automatiquement l'ancienne.
       */
      if (photoFile) {
        data.append(
          'photo_patient',
          photoFile
        );
      }

      /**
       * NOUVEAUX DOCUMENTS
       *
       * Les anciens documents ne sont pas supprimés.
       * Les nouveaux viennent s'ajouter.
       */
      if (documentFiles.length > 0) {
        documentFiles.forEach((file) => {
          data.append(
            'documents[]',
            file
          );
        });
      }

      /**
       * UNE SEULE REQUÊTE API D'UPDATE
       */
      const response = await axios.post(
        `${apiUrl}/admin/patients/${patientId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type':
              'multipart/form-data',
          },
        }
      );

      if (response.data?.success === false) {
        toast.error(
          response.data?.message ||
            'Erreur lors de la mise à jour du dossier'
        );

        return;
      }

      toast.success(
        'Dossier patient mis à jour avec succès !'
      );

      /**
       * Rafraîchissement de la liste parent.
       */
      onPatientUpdated();

      /**
       * Fermeture uniquement après succès.
       */
      onClose();
    } catch (err) {
      console.error(
        'Erreur mise à jour patient :',
        err
      );

      /**
       * Erreurs Laravel 422.
       */
      if (
        err.response?.status === 422
      ) {
        const validationErrors =
          err.response?.data?.errors || {};

        setErrors(validationErrors);

        const firstError =
          Object.values(
            validationErrors
          )?.[0]?.[0];

        toast.error(
          firstError ||
            'Veuillez vérifier les informations saisies.'
        );
      } else {
        toast.error(
          err.response?.data?.message ||
            'Erreur lors de la mise à jour du dossier'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = Math.round(
    ((activeStep + 1) /
      steps.length) *
      100
  );

  const renderStepContent = (step) => {
    switch (step) {
      /**
       * =====================================================
       * ETAPE 1
       * IDENTITE & FICHE PATIENT
       * =====================================================
       */
      case 0:
        return (
          <Box>
            <SectionHeader
              title="État civil & contacts"
              description="Informations permettant d'identifier et de contacter le patient"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Nom du patient *"
                  name="nom_patient"
                  value={
                    formData.nom_patient
                  }
                  onChange={
                    handleChange
                  }
                  error={
                    !!errors.nom_patient
                  }
                  helperText={
                    errors.nom_patient
                  }
                  required
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Prénom du patient *"
                  name="prenom_patient"
                  value={
                    formData.prenom_patient
                  }
                  onChange={
                    handleChange
                  }
                  error={
                    !!errors.prenom_patient
                  }
                  helperText={
                    errors.prenom_patient
                  }
                  required
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Email"
                  name="email_patient"
                  type="email"
                  value={
                    formData.email_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Téléphone"
                  name="telephone_patient"
                  value={
                    formData.telephone_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Lieu de résidence"
                  name="lieu_residence_patient"
                  value={
                    formData.lieu_residence_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Date de naissance"
                  name="date_naissance_patient"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={
                    formData.date_naissance_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Situation familiale"
                  name="situation_familiale_patient"
                  value={
                    formData.situation_familiale_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '180px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  label="Profession"
                  name="profession_patient"
                  value={
                    formData.profession_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '400px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <SectionHeader
              title="Vie professionnelle & personne de confiance"
              description="Contexte professionnel et contact à prévenir en cas de besoin"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  select
                  label="Statut pro."
                  name="statut_pro_patient"
                  value={
                    formData.statut_pro_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                >
                  <MenuItem value="actif">
                    Actif
                  </MenuItem>

                  <MenuItem value="inactif">
                    Inactif
                  </MenuItem>

                  <MenuItem value="retraite">
                    Retraité
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Enfants et âge"
                  name="enfants_age_patient"
                  value={
                    formData.enfants_age_patient
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Personne de confiance (lien / prénom)"
                  name="personne_confiance_lien_prenom"
                  value={
                    formData.personne_confiance_lien_prenom
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Tél. personne de confiance"
                  name="personne_confiance_tel"
                  value={
                    formData.personne_confiance_tel
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Connaissance SOA"
                  name="connaissance_soa"
                  value={
                    formData.connaissance_soa
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    '& .MuiInputBase-root':
                      {
                        alignItems:
                          'flex-start',
                        padding: '12px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Première visite"
                  name="premiere_visite_date"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={
                    formData.premiere_visite_date
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Coordination principale"
                  name="coordination_principale"
                  value={
                    formData.coordination_principale
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <Button
                  variant="outlined"
                  component="label"
                  sx={{
                    borderStyle:
                      'dashed',
                    color: '#4318FF',
                    borderColor:
                      '#4318FF',
                    height: '56px',
                    width: '100%',
                    fontSize:
                      '0.8rem',
                    justifyContent:
                      'flex-start',
                    px: 2,
                    gap: 1,
                  }}
                >
                  <Avatar
                    src={
                      photoFile
                        ? URL.createObjectURL(
                            photoFile
                          )
                        : getFullUrl(
                            currentPhotoPath
                          )
                    }
                    sx={{
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    noWrap
                    sx={{
                      fontSize:
                        'inherit',
                    }}
                  >
                    {photoFile
                      ? photoFile.name
                      : 'Changer la photo'}
                  </Typography>

                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file =
                        e.target.files?.[0];

                      if (file) {
                        setPhotoFile(file);
                      }
                    }}
                  />
                </Button>
              </Grid>
            </Grid>
          </Box>
        );

      /**
       * =====================================================
       * ETAPE 2
       * CHEMIN DE VIE
       * =====================================================
       */
      case 1:
        return (
          <Box>
            <SectionHeader
              title="Cadre de l'entretien"
              description="Contexte de la rencontre avec le patient"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Date de l'entretien"
                  name="date_entretien"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={
                    formData.date_entretien
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Pratique ou coordination"
                  name="pratique_ou_coordination"
                  value={
                    formData.pratique_ou_coordination
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Coordinatrices"
                  name="coordinatrices"
                  value={
                    formData.coordinatrices
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    width: '250px',
                    '& .MuiInputBase-root':
                      {
                        height: '56px',
                      },
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <SectionHeader
              title="Parcours de vie"
              description="Récit libre du patient sur les grandes étapes de sa vie"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={
                    activeField ===
                    'enfance_famille_naissance'
                      ? 10
                      : 4
                  }
                  label="Enfance, famille, naissance"
                  name="enfance_famille_naissance"
                  value={
                    formData.enfance_famille_naissance ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  onFocus={() =>
                    setActiveField(
                      'enfance_famille_naissance'
                    )
                  }
                  onBlur={() =>
                    setActiveField(null)
                  }
                  placeholder="Cliquez pour écrire..."
                  sx={{
                    width: '250px',
                    transition:
                      'all 0.3s ease-in-out',
                    '& .MuiInputBase-root':
                      {
                        backgroundColor:
                          activeField ===
                          'enfance_famille_naissance'
                            ? '#fff'
                            : 'transparent',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={
                    activeField ===
                    'vie_couple_famille'
                      ? 10
                      : 4
                  }
                  label="Vie de couple & famille"
                  name="vie_couple_famille"
                  value={
                    formData.vie_couple_famille ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  onFocus={() =>
                    setActiveField(
                      'vie_couple_famille'
                    )
                  }
                  onBlur={() =>
                    setActiveField(null)
                  }
                  placeholder="Cliquez pour écrire..."
                  sx={{
                    width: '250px',
                    transition:
                      'all 0.3s ease-in-out',
                    '& .MuiInputBase-root':
                      {
                        backgroundColor:
                          activeField ===
                          'vie_couple_famille'
                            ? '#fff'
                            : 'transparent',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={
                    activeField ===
                    'vie_professionnelle'
                      ? 10
                      : 4
                  }
                  label="Vie professionnelle"
                  name="vie_professionnelle"
                  value={
                    formData.vie_professionnelle ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  onFocus={() =>
                    setActiveField(
                      'vie_professionnelle'
                    )
                  }
                  onBlur={() =>
                    setActiveField(null)
                  }
                  placeholder="Cliquez pour écrire..."
                  sx={{
                    width: '250px',
                    transition:
                      'all 0.3s ease-in-out',
                    '& .MuiInputBase-root':
                      {
                        backgroundColor:
                          activeField ===
                          'vie_professionnelle'
                            ? '#fff'
                            : 'transparent',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={
                    activeField ===
                    'vie_aujourd_hui'
                      ? 10
                      : 4
                  }
                  label="Vie aujourd'hui"
                  name="vie_aujourd_hui"
                  value={
                    formData.vie_aujourd_hui ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  onFocus={() =>
                    setActiveField(
                      'vie_aujourd_hui'
                    )
                  }
                  onBlur={() =>
                    setActiveField(null)
                  }
                  placeholder="Cliquez pour écrire..."
                  sx={{
                    width: '250px',
                    transition:
                      'all 0.3s ease-in-out',
                    '& .MuiInputBase-root':
                      {
                        backgroundColor:
                          activeField ===
                          'vie_aujourd_hui'
                            ? '#fff'
                            : 'transparent',
                      },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={
                    activeField ===
                    'resume_chemin_de_vie'
                      ? 10
                      : 4
                  }
                  label="Résumé du chemin de vie"
                  name="resume_chemin_de_vie"
                  value={
                    formData.resume_chemin_de_vie ||
                    ''
                  }
                  onChange={
                    handleChange
                  }
                  onFocus={() =>
                    setActiveField(
                      'resume_chemin_de_vie'
                    )
                  }
                  onBlur={() =>
                    setActiveField(null)
                  }
                  placeholder="Cliquez pour écrire confortablement..."
                  sx={{
                    width: '250px',
                    transition:
                      'all 0.3s ease-in-out',
                    '& .MuiInputBase-root':
                      {
                        backgroundColor:
                          activeField ===
                          'resume_chemin_de_vie'
                            ? '#fff'
                            : 'transparent',
                      },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      /**
       * =====================================================
       * ETAPE 3
       * PARCOURS MEDICAL & HYGIENE
       * =====================================================
       */
      case 2:
        return (
          <Box>
            <SectionHeader
              title="Parcours médical"
              description="Historique de la pathologie et suivi médical actuel"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Parcours général"
                  name="parcours_general"
                  value={
                    formData.parcours_general
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Origine probable décrite"
                  name="origine_probable_decrite"
                  value={
                    formData.origine_probable_decrite
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Déroulé du cancer"
                  name="deroule_cancer"
                  value={
                    formData.deroule_cancer
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Pathologie"
                  name="pathologie_patient"
                  value={
                    formData.pathologie_patient
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Corps médical"
                  name="corps_medical"
                  value={
                    formData.corps_medical
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="État de santé aujourd'hui"
                  name="etat_sante_aujourd_hui"
                  value={
                    formData.etat_sante_aujourd_hui
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Effets indésirables physiques"
                  name="effets_indesirables_physique"
                  value={
                    formData.effets_indesirables_physique
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Effets indésirables psycho-émotionnels"
                  name="effets_indesirables_psy_emo"
                  value={
                    formData.effets_indesirables_psy_emo
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Analyse médicale"
                  name="analyse_medicale"
                  value={
                    formData.analyse_medicale
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Traitement allopathique"
                  name="traitement_allopathique"
                  value={
                    formData.traitement_allopathique
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Traitement alternatif"
                  name="traitement_alternatif"
                  value={
                    formData.traitement_alternatif
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <SectionHeader
              title="Hygiène de vie"
              description="Habitudes quotidiennes pouvant influencer la prise en charge"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Alimentation"
                  name="alimentation"
                  value={
                    formData.alimentation
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Allergies"
                  name="allergies"
                  value={
                    formData.allergies
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Activités physiques"
                  name="activites_physiques"
                  value={
                    formData.activites_physiques
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={3}
              >
                <TextField
                  fullWidth
                  label="Sommeil"
                  name="sommeil"
                  value={
                    formData.sommeil
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Loisirs"
                  name="loisirs"
                  value={
                    formData.loisirs
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <SectionHeader
              title="Situation financière & attentes"
              description="Éléments de suivi administratif et attentes vis-à-vis de SOA"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  select
                  label="Situation financière"
                  name="situation_financiere"
                  value={
                    formData.situation_financiere
                  }
                  onChange={
                    handleChange
                  }
                >
                  <MenuItem value="ras">
                    RAS
                  </MenuItem>

                  <MenuItem value="paiement_partiel_constant">
                    Paiement partiel constant
                  </MenuItem>

                  <MenuItem value="paiement_partiel_temporaire">
                    Paiement partiel temporaire
                  </MenuItem>

                  <MenuItem value="paiement_partiel_ponctuel">
                    Paiement partiel ponctuel
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                sm={8}
              >
                <TextField
                  fullWidth
                  label="Notes financières"
                  name="notes_financieres"
                  value={
                    formData.notes_financieres
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Attentes SOA"
                  name="attentes_soa"
                  value={
                    formData.attentes_soa
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Autres informations"
                  name="autre_info"
                  value={
                    formData.autre_info
                  }
                  onChange={
                    handleChange
                  }
                />
              </Grid>
            </Grid>
          </Box>
        );

      /**
       * =====================================================
       * ETAPE 4
       * DOCUMENTS & COMPTE CLIENT
       * =====================================================
       */
      case 3:
        return (
          <Box>
            
            {existingDocuments.length > 0 && (
              <>
                <SectionHeader
                  title="Documents déjà enregistrés"
                  description="Pièces jointes existantes dans ce dossier"
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 3,
                  }}
                >
                  {existingDocuments.map(
                    (doc) => (
                      <Chip
                        key={
                          doc.id_patient_document
                        }
                        icon={
                          <InsertDriveFileOutlinedIcon />
                        }
                        label={
                          doc.nom_original
                        }
                        component="a"
                        href={getFullUrl(
                          doc.chemin_fichier
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        sx={{
                          bgcolor:
                            '#F4F7FE',
                          color:
                            '#2B3674',
                          fontWeight: 500,
                        }}
                      />
                    )
                  )}
                </Box>

                <Divider sx={{ mb: 3 }} />
              </>
            )}

            
            <SectionHeader
              title="Ajouter de nouveaux documents"
              description="Ces fichiers viendront s'ajouter aux documents existants"
            />

            <Button
              variant="outlined"
              component="label"
              startIcon={
                <CloudUploadIcon />
              }
              sx={{
                borderStyle:
                  'dashed',
                py: 2,
                width: '100%',
                color: '#4318FF',
                borderColor:
                  '#4318FF',
              }}
            >
              Ajouter des documents
              (PDF, images)

              <input
                type="file"
                hidden
                multiple
                onChange={
                  handleAddDocuments
                }
              />
            </Button>

            {documentFiles.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 2,
                }}
              >
                {documentFiles.map(
                  (file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      icon={
                        <InsertDriveFileOutlinedIcon />
                      }
                      label={
                        file.name
                      }
                      onDelete={() =>
                        handleRemoveNewDocument(
                          index
                        )
                      }
                      sx={{
                        bgcolor:
                          '#EDF9F0',
                        color:
                          '#2B3674',
                        fontWeight: 500,
                      }}
                    />
                  )
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            
            <SectionHeader
              title="Compte client / Portail patient"
              description="Informations du compte client associé au patient"
            />

            <Grid
              container
              spacing={2.5}
            >
              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  label="Email client (portail)"
                  name="email_client"
                  type="email"
                  value={
                    formData.email_client
                  }
                  onChange={
                    handleChange
                  }
                  helperText={
                    formData.email_client
                      ? "L'email utilisé pour le portail patient."
                      : "Aucun email client actuellement renseigné."
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  label="Nouveau mot de passe"
                  name="password_client"
                  type="password"
                  value={
                    formData.password_client
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Laisser vide pour conserver l'ancien"
                  helperText="Laisser vide pour ne pas modifier le mot de passe."
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Adresse client"
                  name="addresse_client"
                  value={
                    formData.addresse_client
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Adresse"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Ville client"
                  name="ville_client"
                  value={
                    formData.ville_client
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Ville"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Code postal"
                  name="cp_client"
                  value={
                    formData.cp_client
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Code postal"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
              >
                <TextField
                  fullWidth
                  label="Téléphone client"
                  name="phone_client"
                  value={
                    formData.phone_client
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Téléphone"
                />
              </Grid>
            </Grid>

            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                backgroundColor:
                  '#F4F7FE',
                border:
                  '1px solid #E0E5F2',
                borderRadius:
                  '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize:
                    '0.85rem',
                  color:
                    '#2B3674',
                  fontWeight: 600,
                }}
              >
                Mise à jour du compte client
              </Typography>

              <Typography
                sx={{
                  fontSize:
                    '0.8rem',
                  color:
                    '#A3AED0',
                  mt: 0.5,
                }}
              >
                Les informations du compte
                client seront mises à jour
                uniquement lorsque vous
                cliquerez sur
                « Enregistrer les
                modifications ».
              </Typography>

              <Typography
                sx={{
                  fontSize:
                    '0.8rem',
                  color:
                    '#A3AED0',
                  mt: 0.5,
                }}
              >
                Si le champ mot de passe
                est laissé vide, l'ancien
                mot de passe sera conservé.
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          minHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          backgroundColor:
            '#F4F7FE',
          px: 4,
          py: 2,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
        >
          <MedicalInformationIcon
            sx={{
              color: '#4318FF',
              fontSize: 28,
            }}
          />

          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: '#2B3674',
                lineHeight: 1.2,
              }}
            >
              {fetching
                ? 'Chargement du dossier...'
                : `Modifier le dossier de ${formData.nom_patient} ${formData.prenom_patient}`}
            </Typography>

            {!fetching && (
              <Typography
                variant="caption"
                sx={{
                  color: '#A3AED0',
                }}
              >
                Étape{' '}
                {activeStep + 1} sur{' '}
                {steps.length} —{' '}
                {
                  steps[
                    activeStep
                  ].subtitle
                }
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#A3AED0',
            '&:hover': {
              color: '#2B3674',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <LinearProgress
        variant="determinate"
        value={
          fetching
            ? 0
            : progressPercent
        }
        sx={{
          height: 3,
          backgroundColor:
            '#E0E5F2',
          '& .MuiLinearProgress-bar':
            {
              backgroundColor:
                '#4318FF',
            },
        }}
      />

      {fetching ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent:
              'center',
            alignItems: 'center',
            minHeight:
              '500px',
          }}
        >
          <CircularProgress
            sx={{
              color: '#4318FF',
            }}
          />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              px: 4,
              pt: 3,
              pb: 1,
              backgroundColor:
                '#F8F9FA',
            }}
          >
            <Stepper
              activeStep={
                activeStep
              }
              alternativeLabel
              connector={
                <ColorConnector />
              }
            >
              {steps.map(
                (
                  step,
                  index
                ) => (
                  <Step
                    key={
                      step.label
                    }
                  >
                    <StepButton
                      type="button"
                      onClick={handleStepClick(
                        index
                      )}
                    >
                      <Box
                        sx={{
                          display:
                            'flex',
                          flexDirection:
                            'column',
                          alignItems:
                            'center',
                          gap: 0.5,
                        }}
                      >
                        <CustomStepIcon
                          active={
                            activeStep ===
                            index
                          }
                          completed={
                            activeStep >
                            index
                          }
                          icon={
                            index + 1
                          }
                        />

                        <Typography
                          sx={{
                            fontSize:
                              '0.8rem',
                            fontWeight:
                              600,
                            color:
                              activeStep ===
                              index
                                ? '#2B3674'
                                : '#A3AED0',
                          }}
                        >
                          {
                            step.label
                          }
                        </Typography>
                      </Box>
                    </StepButton>
                  </Step>
                )
              )}
            </Stepper>
          </Box>

          <Box
            component="div"
            onKeyDown={
              handleFormKeyDown
            }
          >
            <DialogContent
              sx={{
                px: 4,
                py: 2.5,
                backgroundColor:
                  '#F8F9FA',
                minHeight:
                  '450px',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  borderRadius:
                    '12px',
                  border:
                    '1px solid #E0E5F2',
                  backgroundColor:
                    '#FFFFFF',
                }}
              >
                {renderStepContent(
                  activeStep
                )}
              </Paper>
            </DialogContent>

            <Divider />

            <DialogActions
              sx={{
                px: 4,
                py: 2,
                backgroundColor:
                  '#F4F7FE',
                justifyContent:
                  'space-between',
              }}
            >
              <Button
                type="button"
                disabled={
                  activeStep === 0 ||
                  loading
                }
                onClick={
                  handleBack
                }
                sx={{
                  fontWeight: 600,
                  color:
                    activeStep === 0
                      ? '#CBD5E1'
                      : '#4318FF',
                }}
              >
                Retour
              </Button>

              <Box>
                <Button
                  type="button"
                  disabled={loading}
                  onClick={
                    onClose
                  }
                  color="inherit"
                  sx={{
                    mr: 2,
                    fontWeight: 600,
                    color:
                      '#A3AED0',
                  }}
                >
                  Annuler
                </Button>

                {activeStep <
                steps.length - 1 ? (
                  <Button
                    type="button"
                    variant="contained"
                    disabled={loading}
                    onClick={
                      handleNext
                    }
                    sx={{
                      backgroundColor:
                        '#4318FF',
                      fontWeight:
                        700,
                      borderRadius:
                        '8px',
                      px: 4,
                      '&:hover':
                        {
                          backgroundColor:
                            '#3311CC',
                        },
                    }}
                  >
                    Suivant
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="contained"
                    disabled={
                      loading
                    }
                    onClick={
                      handleSubmit
                    }
                    sx={{
                      backgroundColor:
                        '#05CD99',
                      fontWeight:
                        700,
                      borderRadius:
                        '8px',
                      px: 4,
                      '&:hover':
                        {
                          backgroundColor:
                            '#04B384',
                        },
                    }}
                  >
                    {loading
                      ? 'Mise à jour...'
                      : 'Enregistrer les modifications'}
                  </Button>
                )}
              </Box>
            </DialogActions>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default EditPatientDialog;
